import { RefObject, useCallback, useEffect, useState } from 'react';

export type ConnectorGeometry = { top: number; height: number };

const geometryFor = (
  listElement: HTMLElement
): Record<string, ConnectorGeometry> => {
  const listTop = listElement.getBoundingClientRect().top;
  const geometry: Record<string, ConnectorGeometry> = {};
  listElement
    .querySelectorAll<HTMLElement>('[data-connector-key]')
    .forEach((wrapper) => {
      if (wrapper.closest('[data-connector-list]') !== listElement) return;
      const key = wrapper.dataset.connectorKey;
      const panel = wrapper.querySelector<HTMLElement>(
        '[data-connector-panel]'
      );
      if (!key || !panel) return;
      const wrapperTop = wrapper.getBoundingClientRect().top;
      const panelRect = panel.getBoundingClientRect();
      geometry[key] = {
        top: listTop - wrapperTop,
        height: panelRect.top + panelRect.height / 2 - listTop,
      };
    });
  return geometry;
};

const isSameGeometry = (
  previous: Record<string, ConnectorGeometry>,
  next: Record<string, ConnectorGeometry>
): boolean => {
  const previousKeys = Object.keys(previous);
  if (previousKeys.length !== Object.keys(next).length) return false;
  return previousKeys.every(
    (key) =>
      next[key] !== undefined &&
      Math.abs(next[key].top - previous[key].top) < 0.5 &&
      Math.abs(next[key].height - previous[key].height) < 0.5
  );
};

export function useHierarchyConnectors(
  listRef: RefObject<HTMLElement>,
  revision: string
): Record<string, ConnectorGeometry> {
  const [connectors, setConnectors] = useState<
    Record<string, ConnectorGeometry>
  >({});

  const measure = useCallback(() => {
    const listElement = listRef.current;
    if (!listElement) return;
    const next = geometryFor(listElement);
    setConnectors((previous) =>
      isSameGeometry(previous, next) ? previous : next
    );
  }, [listRef]);

  useEffect(() => {
    const listElement = listRef.current;
    if (!listElement) return undefined;

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(listElement);
    listElement
      .querySelectorAll<HTMLElement>('[data-connector-panel]')
      .forEach((panel) => observer.observe(panel));
    window.addEventListener('resize', measure);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [listRef, measure, revision]);

  return connectors;
}
